// Wave 2 T3.1: deck insights — per-slide views/references bars, a 14-day
// view trend, and top referrer decks. Pure CSS bars, derived data only.
import { useEffect, useState } from 'react';
import { PanelState } from '../uikit';
import { ArrowBendUpLeft, Eye, Globe } from '@phosphor-icons/react';

interface Insights {
  version: string | null;
  slides: Array<{ slide_num: number; references: number; views: number; external_views?: number }>;
  series: Array<{ day: string; views: number }>;
  referrers: Array<{ deck_id: string; title: string | null; refs: number }>;
}

function Bar({ value, max, color }: { value: number; max: number; color: string }) {
  const w = max > 0 ? Math.max((value / max) * 100, value > 0 ? 4 : 0) : 0;
  return (
    <div style={{ flex: 1, height: 8, background: 'var(--ds-surface-subtle)', borderRadius: 4 }}>
      <div style={{ width: `${w}%`, height: '100%', background: color, borderRadius: 4 }} />
    </div>
  );
}

export default function DeckInsightsPanel({ deckId }: { deckId: string }) {
  const [data, setData] = useState<Insights | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setData(null); setError(null);
    fetch(`/api/decks/${deckId}/insights`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, [deckId, retryKey]);

  if (error) return <PanelState kind="error" title="Couldn't load insights" description="Try reading this project's activity again." action="Retry insights" onAction={() => setRetryKey(k => k + 1)} />;
  if (!data) return <PanelState kind="loading" title="Loading insights" description="Reading slide views and references for this project." />;
  if (!data.version) return <PanelState title="No activity yet" description="Build a presentation first. Slide views and references will appear here as your team uses it." />;

  const maxViews = Math.max(...data.slides.map(s => s.views), 1);
  const maxRefs = Math.max(...data.slides.map(s => s.references), 1);
  const maxDay = Math.max(...data.series.map(d => d.views), 1);
  const totalViews = data.slides.reduce((a, s) => a + s.views, 0);
  const totalRefs = data.slides.reduce((a, s) => a + s.references, 0);
  const totalExternal = data.slides.reduce((a, s) => a + (s.external_views ?? 0), 0);

  // Fill the sparse series into a dense 14-day window so the trend reads
  // as a timeline (empty days as faint stubs) instead of lone floating bars.
  const byDay = new Map(data.series.map(d => [d.day, d.views]));
  const days: Array<{ day: string; views: number }> = [];
  for (let i = 13; i >= 0; i--) {
    const t = new Date(Date.now() - i * 86_400_000);
    const day = t.toISOString().slice(0, 10);
    days.push({ day, views: byDay.get(day) ?? 0 });
  }
  const fmtDay = (d: string) => d.slice(5).replace('-', '/');

  return (
    <div style={{ padding: 16, overflowY: 'auto', height: '100%', fontSize: 12, maxWidth: 620 }}>
      <div style={{ display: 'flex', gap: 18, marginBottom: 16 }}>
        <div><div style={{ fontSize: 20, fontWeight: 700 }}>{totalViews}</div><div style={{ opacity: 0.6 }}>reads ({data.version})</div></div>
        <div><div style={{ fontSize: 20, fontWeight: 700 }}>{totalRefs}</div><div style={{ opacity: 0.6 }}>references</div></div>
        <div><div style={{ fontSize: 20, fontWeight: 700 }}>{totalExternal}</div><div style={{ opacity: 0.6 }}>external reads</div></div>
        <div><div style={{ fontSize: 20, fontWeight: 700 }}>{data.referrers.length}</div><div style={{ opacity: 0.6 }}>referencing decks</div></div>
      </div>

      <div style={{ fontWeight: 600, margin: '12px 0 6px' }}>Per page</div>
      {data.slides.map(s => (
        <div key={s.slide_num} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
          <span style={{ width: 52, flexShrink: 0, opacity: 0.7 }}>Page {s.slide_num}</span>
          <span style={{ width: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, flexShrink: 0, opacity: 0.75 }} title="reads"><Eye size={11} weight="light" />{s.views}</span>
          <Bar value={s.views} max={maxViews} color="var(--ds-accent-soft)" />
          {(s.external_views ?? 0) > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0, opacity: 0.65 }} title="external (share-page) reads"><Globe size={11} weight="light" />{s.external_views}</span>}
          <span style={{ width: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, flexShrink: 0, opacity: 0.75 }} title="references"><ArrowBendUpLeft size={11} weight="light" />{s.references}</span>
          <Bar value={s.references} max={maxRefs} color="var(--ds-success-soft)" />
        </div>
      ))}

      <div style={{ fontWeight: 600, margin: '16px 0 6px' }}>Reads · last 14 days</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 54 }}>
        {days.map(d => (
          <div key={d.day} title={`${d.day}: ${d.views} read${d.views === 1 ? '' : 's'}`} style={{
            flex: 1, maxWidth: 22, borderRadius: 3,
            height: d.views > 0 ? `${Math.max((d.views / maxDay) * 100, 10)}%` : 3,
            background: d.views > 0 ? 'var(--ds-accent-soft)' : 'var(--ds-surface-subtle)',
          }} />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', opacity: 0.45, marginTop: 3 }}>
        <span>{fmtDay(days[0]!.day)}</span>
        <span>today</span>
      </div>

      <div style={{ fontWeight: 600, margin: '16px 0 6px' }}>Referenced by</div>
      {data.referrers.length === 0 && <div style={{ opacity: 0.5 }}>No other deck references this one yet.</div>}
      {data.referrers.map(r => (
        <div key={r.deck_id} style={{ padding: '2px 0' }}>
          <a href={`/deck/${r.deck_id}`} style={{ color: 'var(--ds-accent)', textDecoration: 'none' }}>{r.title ?? r.deck_id}</a>
          <span style={{ opacity: 0.6 }}> · {r.refs} reference{r.refs > 1 ? 's' : ''}</span>
        </div>
      ))}
    </div>
  );
}
